// ModuleCard — a container on the Studio canvas (pen "Modul", "Moduldel",
// "Söm"): a module or a course, drawn as its numbered parts in reading order,
// joined by the accent seam. A course's parts are its modules; each can be
// unfolded to show its own parts. A container whose parts do not fit its kind
// wears a badge, "Not a module shape · {reason}" (soft conformance, D6; every
// reason in its tooltip). Folded (pen "Modul · hopfälld"), it is its title
// and a summary ("3 parts · 1 in progress"), or while a card is held over it
// "Drop to add as part N". A part also held by other containers says so
// ("Also in 2 modules").
//
// The card's body only; where it sits, selection and dragging belong to the
// canvas (components/Studio). Opening the container opens the material that
// holds its parts. While a card is dragged over the container, `dropSlot` is
// the number it would get (B-06).
//
// Each part is acted on in place (partOps.ts): double-click opens THAT part;
// its menu opens it, moves it up or down, or takes it out (the no-drag path,
// D14); its grip drags it to another place in the reading order. A container
// just made (`naming`) shows its title as the inline rename field (RowRename):
// Enter keeps the typed name, Esc the default. Local state is view-ephemeral only: which
// modules are unfolded, a part drag in progress.

export { ModuleCard };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
    Fragment,
    type PointerEvent,
    type ReactNode,
    type SyntheticEvent,
    useRef,
    useState,
} from "react";
import type { NonConformance } from "../../app/containerFacts.js";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import type { MenuItem } from "../Menu/Menu.js";
import { RowOverflow } from "../RowOverflow/RowOverflow.js";
import { RowRename } from "../RowOverflow/RowRename.js";
import {
    type AlsoIn,
    type ContainerCard,
    type ContainerPart,
    containerSummary,
    type PartKind,
} from "../Studio/studioCards.js";
import { moveTarget, type PartIntent } from "./partOps.js";
import { usePartDrag } from "./usePartDrag.js";
import "./ModuleCard.css";

const PART_ICON: Record<PartKind, IconName> = {
    source: "source",
    material: "material",
    module: "module",
    other: "file",
};

// A control inside the card owns its pointer, double-click and keys: none of
// them may start a card drag, open the container or reach the canvas.
const own = (e: SyntheticEvent) => e.stopPropagation();

const ModuleCard = ({
    card,
    dropSlot,
    online = true,
    onPart,
    naming = false,
    onNamed,
    folded = false,
    onToggleFold,
}: {
    readonly card: ContainerCard;
    readonly dropSlot?: number;
    readonly online?: boolean;
    // Absent: the parts are shown only.
    readonly onPart?: (intent: PartIntent) => void;
    readonly naming?: boolean;
    readonly onNamed?: (title: string | null) => void;
    // Folded to its title and a summary (per person; the canvas keeps it).
    readonly folded?: boolean;
    // Absent: it cannot be folded here.
    readonly onToggleFold?: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
    const list = useRef<HTMLOListElement>(null);
    const partDrag = usePartDrag({
        list,
        rows: ":scope > .bilda-module-card__part",
        enabled: onPart !== undefined && online,
        onMove: (part, from, to) => onPart?.({ type: "move", part, from, to }),
    });
    const { drag } = partDrag;
    const toggle = (id: string) =>
        setUnfolded((s) =>
            s.has(id)
                ? new Set([...s].filter((x) => x !== id))
                : new Set([...s, id]),
        );
    const count = card.parts.length;
    const modules = card.parts.filter((p) => p.kind === "module").length;
    const titleOf = (id: string): string =>
        card.parts.find((p) => p.id === id)?.title ?? t`A part`;
    // Each reason in full (the badge's tooltip), and the badge's short one.
    const reasonText = (r: NonConformance): string => {
        switch (r.type) {
            case "container_empty":
                return t`It has no parts yet.`;
            case "part_is_course":
                return t`“${titleOf(r.part)}” is itself a course.`;
            case "part_not_page_or_module":
                return t`“${titleOf(r.part)}” is not a page or a module.`;
            case "part_is_container":
                return t`“${titleOf(r.part)}” is itself a container.`;
            case "part_removed":
                return t`A part was removed from the workspace.`;
            case "unknown":
                return t`A part does not fit this kind.`;
            default:
                return exhaustSwitchCase(r);
        }
    };
    const shortReason = (r: NonConformance): string => {
        switch (r.type) {
            case "container_empty":
                return t`no parts yet`;
            case "part_is_course":
                return t`“${titleOf(r.part)}” is a course`;
            case "part_not_page_or_module":
                return t`“${titleOf(r.part)}” is not a page or a module`;
            case "part_is_container":
                return t`“${titleOf(r.part)}” is a container`;
            case "part_removed":
                return t`a part was removed`;
            case "unknown":
                return t`a part does not fit`;
            default:
                return exhaustSwitchCase(r);
        }
    };
    const reasons =
        card.conformance.type === "not_conforming"
            ? card.conformance.reasons
            : [];
    const [firstReason] = reasons;
    const badge =
        firstReason === undefined ? null : (
            <div
                className="bilda-module-card__badge"
                role="note"
                title={reasons.map(reasonText).join("\n")}
            >
                <Icon name="warning" size="xs" />
                <span className="bilda-module-card__badge-text">
                    {card.kind === "course"
                        ? t`Not a course shape · ${shortReason(firstReason)}`
                        : t`Not a module shape · ${shortReason(firstReason)}`}
                    {reasons.length > 1 ? ` +${reasons.length - 1}` : ""}
                </span>
            </div>
        );
    const kindLabel = (
        <span className="bilda-module-card__kind">
            <Icon name={card.kind} size="xs" />
            {card.kind === "course" ? t`Course` : t`Module`}
        </span>
    );
    const fold =
        onToggleFold === undefined ? null : (
            <button
                type="button"
                className="bilda-module-card__chevron"
                aria-expanded={!folded}
                aria-label={
                    folded ? t`Unfold ${card.title}` : t`Fold ${card.title}`
                }
                onPointerDown={own}
                onDoubleClick={own}
                onKeyDown={own}
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleFold();
                }}
            >
                <Icon name={folded ? "chevron-right" : "chevron-down"} />
            </button>
        );
    const { parts: partCount, inProgress } = containerSummary(card);

    if (folded)
        return (
            <div
                className="bilda-module-card"
                data-kind={card.kind}
                data-folded="true"
            >
                <div className="bilda-module-card__folded">
                    {fold}
                    <Icon name={card.kind} size="sm" />
                    <p
                        className="bilda-module-card__folded-title"
                        title={card.title}
                    >
                        {card.title}
                    </p>
                    <span className="bilda-module-card__summary">
                        {dropSlot !== undefined
                            ? t`Drop to add as part ${dropSlot}`
                            : inProgress > 0
                              ? t`${plural(partCount, { one: "# part", other: "# parts" })} · ${inProgress} in progress`
                              : t`${plural(partCount, { one: "# part", other: "# parts" })}`}
                    </span>
                </div>
                {badge}
            </div>
        );

    const partMenu = (part: ContainerPart, at: number): readonly MenuItem[] => {
        const move = (direction: "up" | "down"): MenuItem => {
            const to = moveTarget(at, count, direction);
            return {
                _tag: "item",
                label: direction === "up" ? t`Move up` : t`Move down`,
                icon: (
                    <Icon
                        name={direction === "up" ? "arrow-up" : "arrow-down"}
                    />
                ),
                disabled: !online || to === undefined,
                onSelect: () =>
                    to === undefined
                        ? undefined
                        : onPart?.({
                              type: "move",
                              part: part.id,
                              from: at,
                              to,
                          }),
            };
        };
        return [
            {
                _tag: "item",
                label: t`Open`,
                icon: <Icon name="expand" />,
                onSelect: () => onPart?.({ type: "open", part: part.id }),
            },
            move("up"),
            move("down"),
            {
                _tag: "item",
                label:
                    card.kind === "course"
                        ? t`Remove from course`
                        : t`Remove from module`,
                icon: <Icon name="unlink" />,
                disabled: !online,
                onSelect: () => onPart?.({ type: "remove", part: part.id, at }),
            },
        ];
    };

    return (
        <div className="bilda-module-card" data-kind={card.kind}>
            <div className="bilda-module-card__head">
                {fold}
                {kindLabel}
                {card.aiAuthored ? <AiMarker variant="chip" /> : null}
                <span className="bilda-module-card__count">
                    {card.kind === "course"
                        ? t`${plural(modules, { one: "# module", other: "# modules" })}`
                        : t`${plural(count, { one: "# part", other: "# parts" })}`}
                </span>
            </div>
            {naming && onNamed !== undefined ? (
                // biome-ignore lint/a11y/noStaticElementInteractions: only keeps the field's keys and pointer from reaching the card (Enter would open it).
                <span
                    className="bilda-module-card__name"
                    onKeyDown={own}
                    onPointerDown={own}
                    onDoubleClick={own}
                >
                    <RowRename
                        initial={card.title}
                        label={
                            card.kind === "course"
                                ? t`Name the course`
                                : t`Name the module`
                        }
                        onSubmit={onNamed}
                        onCancel={() => onNamed(null)}
                    />
                </span>
            ) : (
                <p className="bilda-module-card__title" title={card.title}>
                    {card.title}
                </p>
            )}
            {badge}
            <ol ref={list} className="bilda-module-card__parts">
                {card.parts.map((part, i) => (
                    <Fragment key={part.id}>
                        {i > 0 ? (
                            <li
                                className="bilda-module-card__seam"
                                aria-hidden="true"
                            />
                        ) : null}
                        <Part
                            part={part}
                            number={i + 1}
                            unfolded={unfolded.has(part.id)}
                            onToggle={() => toggle(part.id)}
                            dragged={drag?.part === part.id}
                            landing={partDrag.landingAt(i)}
                            actions={
                                onPart === undefined
                                    ? null
                                    : {
                                          menu: partMenu(part, i),
                                          onOpen: (id) =>
                                              onPart({
                                                  type: "open",
                                                  part: id,
                                              }),
                                          onGripDown: partDrag.start(
                                              part.id,
                                              i,
                                          ),
                                          onGripMove: partDrag.move,
                                          onGripUp: partDrag.end,
                                          onGripCancel: partDrag.cancel,
                                      }
                            }
                        />
                    </Fragment>
                ))}
                {dropSlot === undefined ? null : (
                    <>
                        <li
                            className="bilda-module-card__seam"
                            aria-hidden="true"
                        />
                        <li className="bilda-module-card__drop-slot">
                            {t`Drop to add as part ${dropSlot}`}
                        </li>
                    </>
                )}
            </ol>
        </div>
    );
};

type PartActions = {
    readonly menu: readonly MenuItem[];
    readonly onOpen: (id: string) => void;
    readonly onGripDown: (e: PointerEvent<HTMLButtonElement>) => void;
    readonly onGripMove: (e: PointerEvent<HTMLButtonElement>) => void;
    readonly onGripUp: () => void;
    readonly onGripCancel: () => void;
};

// The same document in other binders, said on its row ("Also in 2 modules"):
// changing it here changes it there too.
const AlsoInLabel = ({ alsoIn }: { readonly alsoIn: AlsoIn }): ReactNode => {
    const { t } = useLingui();
    const { modules, courses } = alsoIn;
    if (modules + courses === 0) return null;
    const text =
        courses === 0
            ? t`${plural(modules, { one: "Also in # module", other: "Also in # modules" })}`
            : modules === 0
              ? t`${plural(courses, { one: "Also in # course", other: "Also in # courses" })}`
              : t`Also in ${modules + courses} containers`;
    return <span className="bilda-module-card__also-in">{text}</span>;
};

// One numbered part. A module part (in a course) folds its own parts away
// behind a toggle that never starts a drag or opens the course.
const Part = ({
    part,
    number,
    unfolded,
    onToggle,
    dragged,
    landing,
    actions,
}: {
    readonly part: ContainerPart;
    readonly number: number;
    readonly unfolded: boolean;
    readonly onToggle: () => void;
    readonly dragged: boolean;
    // A dragged part would land here: before or after this one.
    readonly landing: "before" | "after" | undefined;
    // Null: shown only.
    readonly actions: PartActions | null;
}): ReactNode => {
    const { t } = useLingui();
    const foldable = part.kind === "module";
    const inner = part.parts.length;
    const open = (id: string) => (e: SyntheticEvent) => {
        e.stopPropagation();
        actions?.onOpen(id);
    };
    return (
        <li
            className="bilda-module-card__part"
            data-kind={part.kind}
            data-planned={part.planned ? "true" : undefined}
            data-dragged={dragged ? "true" : undefined}
            data-landing={landing}
            data-part-id={part.id}
            onDoubleClick={actions === null ? undefined : open(part.id)}
        >
            <div className="bilda-module-card__part-row">
                {actions === null ? null : (
                    <button
                        type="button"
                        className="bilda-module-card__grip"
                        aria-label={t`Drag to reorder ${part.title}`}
                        tabIndex={-1}
                        onPointerDown={actions.onGripDown}
                        onPointerMove={actions.onGripMove}
                        onPointerUp={actions.onGripUp}
                        onPointerCancel={actions.onGripCancel}
                        onDoubleClick={own}
                        onClick={own}
                    >
                        <Icon name="grip" size="xs" />
                    </button>
                )}
                <span className="bilda-module-card__number">{number}</span>
                <Icon name={PART_ICON[part.kind]} size="sm" />
                <span
                    className="bilda-module-card__part-title"
                    title={part.title}
                >
                    {part.title}
                </span>
                <AlsoInLabel alsoIn={part.alsoIn} />
                {part.planned ? (
                    <span className="bilda-module-card__queued">
                        {t`Queued`}
                    </span>
                ) : part.aiAuthored ? (
                    <AiMarker variant="chip" />
                ) : null}
                {foldable ? (
                    <button
                        type="button"
                        className="bilda-module-card__fold"
                        aria-expanded={unfolded}
                        aria-label={
                            unfolded
                                ? t`Hide the parts of ${part.title}`
                                : t`Show the parts of ${part.title}`
                        }
                        onPointerDown={own}
                        onDoubleClick={own}
                        onKeyDown={own}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                    >
                        {t`${plural(inner, { one: "# part", other: "# parts" })}`}
                        <Icon
                            name={unfolded ? "chevron-up" : "chevron-down"}
                            size="xs"
                        />
                    </button>
                ) : null}
                {actions === null ? null : (
                    // biome-ignore lint/a11y/noStaticElementInteractions: only keeps the menu's own pointer, double-click and keys from reaching the card.
                    <span
                        className="bilda-module-card__part-menu"
                        onPointerDown={own}
                        onDoubleClick={own}
                        onKeyDown={own}
                    >
                        <RowOverflow
                            label={t`Actions for ${part.title}`}
                            items={actions.menu}
                        />
                    </span>
                )}
            </div>
            {foldable && unfolded && inner > 0 ? (
                <ol className="bilda-module-card__subparts">
                    {part.parts.map((sub, i) => (
                        <li
                            key={sub.id}
                            className="bilda-module-card__subpart"
                            data-part-id={sub.id}
                            onDoubleClick={
                                actions === null ? undefined : open(sub.id)
                            }
                        >
                            <span className="bilda-module-card__subnumber">
                                {`${number}.${i + 1}`}
                            </span>
                            <Icon name={PART_ICON[sub.kind]} size="xs" />
                            <span
                                className="bilda-module-card__part-title"
                                title={sub.title}
                            >
                                {sub.title}
                            </span>
                        </li>
                    ))}
                </ol>
            ) : null}
        </li>
    );
};
